import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { NOTE_SUBTHEMES, TASK_CATEGORIES, REFERENCE_LABEL, REFERENCE_PLACEHOLDER, isGrouped } from '../constants';

function renderSubthemeOptions(subthemes) {
  if (!subthemes) return null;
  if (isGrouped(subthemes)) {
    return subthemes.groups.map(g => (
      <optgroup key={g.label} label={g.label}>
        {g.items.map(s => <option key={s} value={s}>{s}</option>)}
      </optgroup>
    ));
  }
  return subthemes.map(s => <option key={s} value={s}>{s}</option>);
}

function emptyForm() {
  return {
    note_date: new Date().toISOString().split('T')[0],
    title: '', content: '',
    category: '', subtheme: '', reference: '', child_id: '',
  };
}

export default function NotesPage() {
  const [notes, setNotes] = useState([]);
  const [children, setChildren] = useState([]);
  const [filterCat, setFilterCat] = useState('');
  const [filterSub, setFilterSub] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDossier, setFilterDossier] = useState('');
  const [dossiers, setDossiers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    const params = {};
    if (filterCat) params.category = filterCat;
    if (filterSub) params.subtheme = filterSub;
    if (filterSearch) params.search = filterSearch;
    if (filterDossier) params.reference = filterDossier;
    try {
      const r = await api.get('/notes', { params });
      setNotes(r.data);
    } catch {}
  }, [filterCat, filterSub, filterSearch, filterDossier]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/children').then(r => setChildren(r.data)).catch(() => {});
    api.get('/tasks/meta/dossiers').then(r => setDossiers(r.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm(), category: filterCat || '' });
    setShowModal(true);
  };

  const openEdit = (note) => {
    setEditingId(note.id);
    setForm({
      note_date: note.note_date.split('T')[0],
      title: note.title || '',
      content: note.content,
      category: note.category || '',
      subtheme: note.subtheme || '',
      reference: note.reference || '',
      child_id: note.child_id || '',
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      category: form.category || null,
      subtheme: form.subtheme || null,
      reference: form.reference || null,
      child_id: form.child_id || null,
      title: form.title || null,
    };
    try {
      if (editingId) await api.put(`/notes/${editingId}`, payload);
      else await api.post('/notes', payload);
      setShowModal(false);
      setForm(emptyForm());
      setEditingId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    await api.delete(`/notes/${id}`);
    load();
  };

  const currentSubthemes = NOTE_SUBTHEMES[form.category];
  const useChildDropdown = form.category === 'enfants';
  const refLabel = REFERENCE_LABEL[form.category] || 'Référence (optionnel)';
  const refPlaceholder = REFERENCE_PLACEHOLDER[form.category] || '';

  // Groupement par dossier si catégorie travail
  const groupedNotes = React.useMemo(() => {
    if (filterCat !== 'travail') return null;
    const groups = {};
    notes.forEach(n => {
      const key = n.reference && n.reference.trim() ? n.reference : '(Sans dossier)';
      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    });
    return Object.keys(groups).sort((a, b) => {
      if (a === '(Sans dossier)') return 1;
      if (b === '(Sans dossier)') return -1;
      return a.localeCompare(b, 'fr');
    }).map(k => ({ name: k, notes: groups[k] }));
  }, [notes, filterCat]);

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Notes</h1>
          <p>{notes.length} note{notes.length !== 1 ? 's' : ''} · organisées par dossier et thème</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nouvelle note</button>
      </div>

      {/* Filtres */}
      <div className="task-filters">
        <button className={`filter-btn ${filterCat === '' ? 'active' : ''}`} onClick={() => { setFilterCat(''); setFilterSub(''); setFilterDossier(''); }}>Toutes</button>
        {TASK_CATEGORIES.map(c => (
          <button key={c.value} className={`filter-btn ${filterCat === c.value ? 'active' : ''}`} onClick={() => { setFilterCat(filterCat === c.value ? '' : c.value); setFilterSub(''); setFilterDossier(''); }}>
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <input className="form-input" placeholder="🔍 Rechercher dans les notes..." value={filterSearch} onChange={e => setFilterSearch(e.target.value)} style={{ maxWidth: 280, fontSize: '0.82rem' }} />
        {filterCat && (
          <select className="form-input" style={{ maxWidth: 240, fontSize: '0.82rem' }} value={filterSub} onChange={e => setFilterSub(e.target.value)}>
            <option value="">Tous sous-thèmes</option>
            {renderSubthemeOptions(NOTE_SUBTHEMES[filterCat])}
          </select>
        )}
        {filterCat === 'travail' && dossiers.length > 0 && (
          <select className="form-input" style={{ maxWidth: 240, fontSize: '0.82rem' }} value={filterDossier} onChange={e => setFilterDossier(e.target.value)}>
            <option value="">Tous dossiers</option>
            {dossiers.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        )}
      </div>

      {notes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-emoji">📝</div>
          <p>Aucune note. Créez votre première note taguée !</p>
        </div>
      ) : groupedNotes ? (
        <div>
          {groupedNotes.map(group => (
            <div key={group.name} style={{ marginBottom: 20 }}>
              <div className="day-section-title">
                <span className="section-emoji">📁</span>
                {group.name}
                <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                  {group.notes.length} note{group.notes.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {group.notes.map(note => <NoteCard key={note.id} note={note} onEdit={openEdit} onDelete={deleteNote} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notes.map(note => <NoteCard key={note.id} note={note} onEdit={openEdit} onDelete={deleteNote} />)}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" /* Pas de fermeture au clic extérieur */>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <h2>{editingId ? 'Modifier la note' : 'Nouvelle note'}</h2>
            <form onSubmit={save}>
              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input className="form-input" type="date" value={form.note_date} onChange={e => setForm({ ...form, note_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Titre (optionnel)</label>
                  <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titre court" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie</label>
                  <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value, subtheme: '', reference: '', child_id: '' })}>
                    <option value="">— Aucune —</option>
                    {TASK_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sous-thème</label>
                  <select className="form-input" value={form.subtheme} onChange={e => setForm({ ...form, subtheme: e.target.value })} disabled={!form.category}>
                    <option value="">—</option>
                    {renderSubthemeOptions(currentSubthemes)}
                  </select>
                </div>
              </div>

              {form.category && (
                <div className="form-group">
                  <label>{refLabel}</label>
                  {useChildDropdown ? (
                    <select className="form-input" value={form.child_id} onChange={e => setForm({ ...form, child_id: e.target.value })}>
                      <option value="">— Choisir un enfant —</option>
                      {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  ) : (
                    <input
                      className="form-input"
                      value={form.reference}
                      onChange={e => setForm({ ...form, reference: e.target.value })}
                      placeholder={refPlaceholder}
                      list={form.category === 'travail' ? 'dossiers-list-note' : undefined}
                    />
                  )}
                  {form.category === 'travail' && (
                    <datalist id="dossiers-list-note">
                      {dossiers.map(d => <option key={d} value={d} />)}
                    </datalist>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Contenu</label>
                <textarea className="form-input" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Votre note..." style={{ minHeight: 200, lineHeight: 1.7 }} required />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{editingId ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function NoteCard({ note, onEdit, onDelete }) {
  const catLabel = TASK_CATEGORIES.find(c => c.value === note.category);
  const refDisplay = note.child_id ? `👶 ${note.child_name || 'Enfant'}` : (note.reference ? `📁 ${note.reference}` : null);
  const preview = note.content.length > 200 ? note.content.slice(0, 200) + '…' : note.content;
  const dateStr = new Date(note.note_date + 'T00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="card" style={{ cursor: 'pointer', padding: 16 }} onClick={() => onEdit(note)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          {note.title && <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>{note.title}</div>}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>📅 {dateStr}</span>
            {catLabel && <span className={`badge badge-${note.category}`}>{catLabel.emoji} {catLabel.label}</span>}
            {note.subtheme && <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }}>{note.subtheme}</span>}
            {refDisplay && <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', fontWeight: 600 }}>{refDisplay}</span>}
          </div>
        </div>
        <button className="btn-ghost" onClick={e => { e.stopPropagation(); onDelete(note.id); }} title="Supprimer">🗑️</button>
      </div>
      <div style={{ fontSize: '0.88rem', lineHeight: 1.6, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{preview}</div>
    </div>
  );
}
